import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { publicAPI } from '../../lib/api';
import { formatCurrency, formatTime } from '../../lib/utils';
import { toast } from 'sonner';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Calendar } from '../ui/calendar';
import { Checkbox } from '../ui/checkbox';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../ui/accordion';
import {
  Check,
  CaretLeft,
  CaretRight,
  Clock,
  User,
  Scissors,
  CalendarBlank,
  EnvelopeSimple,
  CheckCircle,
  DownloadSimple,
  GoogleLogo,
  MapPin,
  Phone,
  Star,
  CurrencyGbp,
  MagnifyingGlass,
  SpinnerGap,
} from '@phosphor-icons/react';
import { format, addWeeks, addHours, isBefore, startOfDay } from 'date-fns';

// Module-level helper (avoids remount on parent re-render)
const renderPublicServiceCard = (service, booking, setBooking) => (
  <div
    key={service.id}
    className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
      booking.service?.id === service.id ? 'border-secondary bg-secondary/5' : 'border-transparent bg-white hover:bg-muted/40'
    }`}
    onClick={() => setBooking({ ...booking, service, practitioner: null, noPreference: false, date: null, time: null })}
    data-testid={`service-option-${service.id}`}
    style={{ minHeight: '48px' }}
  >
    <div className="flex items-center justify-between gap-3">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="font-medium text-sm sm:text-base truncate">{service.name}</p>
          {service.is_popular && (
            <Badge variant="secondary" className="text-[10px] gap-1" data-testid={`popular-badge-${service.id}`}>
              <Star size={10} weight="fill" /> Popular
            </Badge>
          )}
          {service.deposit_required && (
            <Badge variant="outline" className="text-[10px] gap-1 text-amber-700 border-amber-300 bg-amber-50" data-testid={`deposit-badge-${service.id}`}>
              <CurrencyGbp size={10} /> Deposit
            </Badge>
          )}
        </div>
        <p className="text-xs text-muted-foreground flex items-center gap-1.5 mt-0.5">
          <Clock size={12} /> {service.duration} mins
          {service.description && <span className="hidden sm:inline truncate">· {service.description}</span>}
        </p>
      </div>
      <p className="font-semibold text-base whitespace-nowrap">{formatCurrency(service.price)}</p>
    </div>
  </div>
);

const steps = [
  { id: 1, title: 'Select Service', icon: Scissors },
  { id: 2, title: 'Choose Practitioner', icon: User },
  { id: 3, title: 'Pick Date & Time', icon: CalendarBlank },
  { id: 4, title: 'Your Details', icon: EnvelopeSimple },
  { id: 5, title: 'Confirm & Book', icon: Check },
];

function generateICS(booking, confirmation) {
  const dateStr = booking.date ? format(booking.date, 'yyyyMMdd') : '';
  const startTime = (booking.time || '').replace(':', '');
  const endTime = (confirmation?.end_time || '').replace(':', '');
  const summary = `${booking.service?.name || 'Appointment'} at ${confirmation?.clinic_name || ''}`;
  const location = confirmation?.clinic_address || '';
  const description = `Service: ${booking.service?.name}\\nPractitioner: ${confirmation?.practitioner_name || ''}\\nDuration: ${booking.service?.duration} minutes`;
  return [
    'BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//Cliniqueo//Booking//EN', 'BEGIN:VEVENT',
    `DTSTART:${dateStr}T${startTime}00`, `DTEND:${dateStr}T${endTime}00`,
    `SUMMARY:${summary}`, `LOCATION:${location}`, `DESCRIPTION:${description}`,
    'STATUS:CONFIRMED', 'END:VEVENT', 'END:VCALENDAR',
  ].join('\r\n');
}

function getGoogleCalUrl(booking, confirmation) {
  const dateStr = booking.date ? format(booking.date, 'yyyyMMdd') : '';
  const startTime = (booking.time || '').replace(':', '');
  const endTime = (confirmation?.end_time || '').replace(':', '');
  const title = encodeURIComponent(`${booking.service?.name || 'Appointment'} at ${confirmation?.clinic_name || ''}`);
  const location = encodeURIComponent(confirmation?.clinic_address || '');
  const details = encodeURIComponent(`Practitioner: ${confirmation?.practitioner_name || ''}\nDuration: ${booking.service?.duration} minutes`);
  return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${dateStr}T${startTime}00/${dateStr}T${endTime}00&location=${location}&details=${details}`;
}

export const PublicBookingPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const orgParam = searchParams.get('org');

  const [orgId, setOrgId] = useState(orgParam || '');
  const [currentStep, setCurrentStep] = useState(1);
  const [organisation, setOrganisation] = useState(null);
  const [services, setServices] = useState([]);
  const [practitioners, setPractitioners] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [slotPractitionerMap, setSlotPractitionerMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [bookingComplete, setBookingComplete] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  // Build 6a — Stripe return + retry state
  const [paymentReturnState, setPaymentReturnState] = useState(null);
  // shape: { kind: 'success' | 'cancelled' | 'expired' | 'polling', appointment_id, status, payment_status, can_retry }
  const [retrying, setRetrying] = useState(false);

  // Returning client
  const [isReturning, setIsReturning] = useState(false);
  const [lookupEmail, setLookupEmail] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);

  // Available dates (for greying out fully-booked days)
  const [availableDates, setAvailableDates] = useState(null); // null = not loaded yet, [] = loaded
  const [datesLoading, setDatesLoading] = useState(false);

  const [booking, setBooking] = useState({
    service: null,
    practitioner: null, // null = "no preference"
    noPreference: false,
    date: null,
    time: null,
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    gdprConsent: false,
    marketingConsent: false,
    smsConsent: false,
  });

  // Booking window constraints
  const advanceWeeks = organisation?.booking_settings?.advance_booking_window || organisation?.booking_settings?.advance_weeks || 8;
  const minNoticeHours = organisation?.booking_settings?.minimum_notice_hours || organisation?.booking_settings?.min_notice_hours || 2;
  const maxDate = addWeeks(new Date(), advanceWeeks);
  const minDate = startOfDay(addHours(new Date(), minNoticeHours));

  useEffect(() => {
    loadInitialData();
  }, []);

  // Build 6a — handle return from Stripe Checkout (success / cancelled).
  useEffect(() => {
    const paymentStatusParam = searchParams.get('payment_status');
    const aptId = searchParams.get('appointment_id');
    if (!paymentStatusParam || !aptId) return;

    let cancelled = false;
    const start = Date.now();
    const POLL_TIMEOUT_MS = 15000;

    if (paymentStatusParam === 'success') {
      setPaymentReturnState({ kind: 'polling', appointment_id: aptId });
      const poll = async () => {
        try {
          const res = await publicAPI.getBookingPaymentStatus(aptId);
          if (cancelled) return;
          if (res.data.payment_status === 'deposit_paid' || res.data.payment_status === 'paid') {
            setPaymentReturnState({ kind: 'success', ...res.data });
            return;
          }
          if (Date.now() - start > POLL_TIMEOUT_MS) {
            // Webhook hasn't landed yet — show success with note.
            setPaymentReturnState({ kind: 'success', appointment_id: aptId, late: true });
            return;
          }
          setTimeout(poll, 1500);
        } catch (e) {
          setPaymentReturnState({ kind: 'cancelled', appointment_id: aptId, error: 'lookup_failed' });
        }
      };
      poll();
    } else if (paymentStatusParam === 'cancelled') {
      // Look up the appointment to know whether retry is still possible.
      publicAPI.getBookingPaymentStatus(aptId)
        .then((res) => {
          if (cancelled) return;
          if (res.data.status === 'cancelled') {
            setPaymentReturnState({ kind: 'expired', appointment_id: aptId });
          } else {
            setPaymentReturnState({ kind: 'cancelled', ...res.data });
          }
        })
        .catch(() => {
          if (!cancelled) setPaymentReturnState({ kind: 'cancelled', appointment_id: aptId });
        });
    }

    return () => { cancelled = true; };
  }, [searchParams]);

  const handleRetryPayment = async (appointmentId) => {
    setRetrying(true);
    try {
      const res = await publicAPI.retryBookingPayment(appointmentId);
      if (res.data?.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (err) {
      if (err.response?.status === 410) {
        setPaymentReturnState({ kind: 'expired', appointment_id: appointmentId });
      } else {
        toast.error(err.response?.data?.detail || 'Could not restart payment');
      }
    } finally {
      setRetrying(false);
    }
  };

  const handleClearPaymentReturn = () => {
    setPaymentReturnState(null);
    // Strip payment_status / session_id / appointment_id from the URL.
    const params = new URLSearchParams(searchParams);
    params.delete('payment_status');
    params.delete('session_id');
    params.delete('appointment_id');
    setSearchParams(params, { replace: true });
  };

  const loadInitialData = async () => {
    try {
      let org;
      if (orgParam) {
        org = (await publicAPI.getOrganisation(orgParam)).data;
      } else {
        org = (await publicAPI.getDefaultOrganisation()).data;
      }
      setOrgId(org.id);
      setOrganisation(org);
      const servicesRes = await publicAPI.getServices(org.id);
      setServices(servicesRes.data);
    } catch {
      toast.error('Unable to load booking page. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const loadPractitioners = useCallback(async (serviceId) => {
    try {
      const response = await publicAPI.getPractitioners(orgId, serviceId);
      setPractitioners(response.data);
    } catch {
      console.error('Failed to load practitioners');
    }
  }, [orgId]);

  const loadAvailability = useCallback(async (practitionerId, date, serviceId) => {
    setSlotsLoading(true);
    try {
      const dateStr = format(date, 'yyyy-MM-dd');
      const response = await publicAPI.getAvailability(orgId, practitionerId, dateStr, serviceId);
      setAvailableSlots(response.data.available_slots || []);
      setSlotPractitionerMap(response.data.slot_practitioner_map || {});
    } catch {
      setAvailableSlots([]);
      setSlotPractitionerMap({});
    } finally {
      setSlotsLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    if (booking.service && orgId) loadPractitioners(booking.service.id);
  }, [booking.service, orgId, loadPractitioners]);

  useEffect(() => {
    if (booking.date && booking.service && orgId) {
      const pracId = booking.noPreference ? 'any' : booking.practitioner?.id;
      if (pracId) loadAvailability(pracId, booking.date, booking.service.id);
    }
  }, [booking.practitioner, booking.noPreference, booking.date, booking.service, orgId, loadAvailability]);

  // Load available dates when practitioner is picked (for calendar greying)
  const loadAvailableDates = useCallback(async () => {
    if (!orgId || !booking.service) return;
    const pracId = booking.noPreference ? 'any' : booking.practitioner?.id;
    if (!pracId) return;
    setDatesLoading(true);
    try {
      const res = await publicAPI.getAvailableDates(orgId, pracId, booking.service.id, advanceWeeks);
      setAvailableDates(new Set(res.data.available_dates || []));
    } catch {
      setAvailableDates(null); // fail open — allow all dates
    } finally {
      setDatesLoading(false);
    }
  }, [orgId, booking.service, booking.practitioner, booking.noPreference, advanceWeeks]);

  useEffect(() => {
    if ((booking.practitioner || booking.noPreference) && booking.service) {
      loadAvailableDates();
    }
  }, [booking.practitioner, booking.noPreference, booking.service, loadAvailableDates]);

  const handleClientLookup = async () => {
    if (!lookupEmail || !orgId) return;
    setLookupLoading(true);
    try {
      const res = await publicAPI.clientLookup({ organisation_id: orgId, email: lookupEmail });
      // Privacy-safe: backend now returns only {found: true|false}, no PII.
      // We confirm to the user without echoing their stored details — they'll
      // re-type their name/phone, and the backend matches on email at booking time.
      setBooking(prev => ({ ...prev, email: lookupEmail }));
      if (res.data.found) {
        toast.success('Welcome back! Please confirm your details below.');
      } else {
        toast.info('No account found. Please fill in your details.');
      }
    } catch (error) {
      if (error.response?.status === 429) {
        toast.error('Too many requests, please try again shortly');
      } else {
        toast.error('Failed to look up account.');
      }
    } finally {
      setLookupLoading(false);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      // If "no preference", resolve practitioner from slot map
      let practitionerId = booking.practitioner?.id;
      if (booking.noPreference && booking.time && slotPractitionerMap[booking.time]) {
        practitionerId = slotPractitionerMap[booking.time];
      }

      const res = await publicAPI.createBooking({
        organisation_id: orgId,
        service_id: booking.service.id,
        practitioner_id: practitionerId,
        date: format(booking.date, 'yyyy-MM-dd'),
        start_time: booking.time,
        client_first_name: booking.firstName,
        client_last_name: booking.lastName,
        client_email: booking.email,
        client_phone: booking.phone || null,
        gdpr_data_consent: booking.gdprConsent,
        marketing_consent: booking.marketingConsent,
        sms_marketing_consent: booking.smsConsent,
      });

      // Build 6a — deposit flow: redirect to Stripe Checkout.
      if (res.data?.requires_deposit_payment && res.data?.checkout_url) {
        window.location.href = res.data.checkout_url;
        return;
      }

      setConfirmation(res.data);
      setBookingComplete(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create booking. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownloadICS = () => {
    const ics = generateICS(booking, confirmation);
    const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `appointment-${confirmation?.appointment_id || 'booking'}.ics`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1: return booking.service;
      case 2: return booking.practitioner || booking.noPreference;
      case 3: return booking.date && booking.time;
      case 4: return booking.firstName && booking.lastName && booking.email && booking.gdprConsent;
      case 5: return true;
      default: return false;
    }
  };

  // Deposit calculation
  const depositRules = organisation?.deposit_rules || {};
  const depositRequired = depositRules.require_deposits && booking.service?.deposit_required;
  const depositAmount = depositRequired
    ? depositRules.deposit_type === 'percent'
      ? (booking.service?.price || 0) * (depositRules.deposit_value || 0) / 100
      : Math.min(depositRules.deposit_value || 0, booking.service?.price || 0)
    : 0;

  // Group services by category → subcategory (sorted)
  const CATEGORY_ORDER = ['Injectables', 'Facials', 'Skin', 'Body', 'Wellness', 'Hair', 'Other'];
  const groupedServices = services.reduce((acc, svc) => {
    const cat = svc.category || 'Other';
    const sub = svc.subcategory || '';
    if (!acc[cat]) acc[cat] = { items: [], bySub: {} };
    if (sub) {
      if (!acc[cat].bySub[sub]) acc[cat].bySub[sub] = [];
      acc[cat].bySub[sub].push(svc);
    } else {
      acc[cat].items.push(svc);
    }
    return acc;
  }, {});
  const sortServices = (arr) => [...arr].sort((a, b) => {
    const ao = a.display_order ?? 999999;
    const bo = b.display_order ?? 999999;
    if (ao !== bo) return ao - bo;
    return a.name.localeCompare(b.name);
  });
  const orderedCategories = Object.keys(groupedServices).sort((a, b) => {
    const ai = CATEGORY_ORDER.indexOf(a);
    const bi = CATEGORY_ORDER.indexOf(b);
    if (ai === -1 && bi === -1) return a.localeCompare(b);
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  });

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-muted-foreground">Loading booking page...</div>
      </div>
    );
  }

  // ====== STRIPE RETURN SCREEN (Build 6a) ======
  if (paymentReturnState) {
    const aptId = paymentReturnState.appointment_id;
    if (paymentReturnState.kind === 'polling') {
      return (
        <div className="max-w-lg mx-auto py-12 px-4" data-testid="payment-polling">
          <Card className="dashboard-card text-center">
            <CardContent className="pt-8 pb-8">
              <SpinnerGap size={40} className="text-secondary animate-spin mx-auto mb-4" />
              <h2 className="text-2xl font-serif font-semibold mb-2">Confirming your payment…</h2>
              <p className="text-muted-foreground">Just a moment — we're verifying your deposit with Stripe.</p>
            </CardContent>
          </Card>
        </div>
      );
    }
    if (paymentReturnState.kind === 'success') {
      return (
        <div className="max-w-lg mx-auto py-12 px-4" data-testid="payment-success">
          <Card className="dashboard-card text-center">
            <CardContent className="pt-8 pb-8">
              <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                <CheckCircle size={40} className="text-green-600" />
              </div>
              <h2 className="text-2xl font-serif font-semibold mb-2">Deposit Received — You're Booked!</h2>
              <p className="text-muted-foreground mb-6">
                {paymentReturnState.late
                  ? 'Your payment was successful. We may take a few more seconds to mark your booking as paid — your appointment is held in either case. A confirmation email will follow.'
                  : 'Thank you — your appointment is confirmed and a confirmation email is on its way.'}
              </p>
              <Button className="btn-primary" onClick={handleClearPaymentReturn} data-testid="payment-success-done-btn">
                Done
              </Button>
            </CardContent>
          </Card>
        </div>
      );
    }
    if (paymentReturnState.kind === 'expired') {
      return (
        <div className="max-w-lg mx-auto py-12 px-4" data-testid="payment-expired">
          <Card className="dashboard-card text-center">
            <CardContent className="pt-8 pb-8">
              <div className="w-20 h-20 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-6">
                <Clock size={40} className="text-amber-600" />
              </div>
              <h2 className="text-2xl font-serif font-semibold mb-2">Booking expired</h2>
              <p className="text-muted-foreground mb-6">Your slot wasn't paid for in time, so we released it. Please start a new booking.</p>
              <Button className="btn-primary" onClick={() => { handleClearPaymentReturn(); window.location.reload(); }} data-testid="payment-expired-restart-btn">
                Start Again
              </Button>
            </CardContent>
          </Card>
        </div>
      );
    }
    // cancelled
    return (
      <div className="max-w-lg mx-auto py-12 px-4" data-testid="payment-cancelled">
        <Card className="dashboard-card text-center">
          <CardContent className="pt-8 pb-8">
            <div className="w-20 h-20 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-6">
              <CurrencyGbp size={40} className="text-amber-600" />
            </div>
            <h2 className="text-2xl font-serif font-semibold mb-2">Payment cancelled</h2>
            <p className="text-muted-foreground mb-6">
              We've held your slot for a few minutes. Try again or contact the clinic to pay another way.
            </p>
            <div className="flex gap-3 justify-center">
              <Button
                className="btn-primary"
                disabled={retrying || paymentReturnState.can_retry === false}
                onClick={() => handleRetryPayment(aptId)}
                data-testid="payment-retry-btn"
              >
                {retrying ? 'Restarting…' : 'Try Again'}
              </Button>
              <Button variant="outline" onClick={() => { handleClearPaymentReturn(); window.location.reload(); }} data-testid="payment-cancelled-restart-btn">
                Start Over
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ====== CONFIRMATION PAGE ======
  if (bookingComplete && confirmation) {
    return (
      <div className="max-w-lg mx-auto py-12 px-4" data-testid="booking-success">
        <Card className="dashboard-card text-center">
          <CardContent className="pt-8 pb-8">
            <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
              <CheckCircle size={40} className="text-green-600" />
            </div>
            <h2 className="text-2xl font-serif font-semibold mb-2">You're Booked!</h2>
            <p className="text-muted-foreground mb-6">
              Your appointment is confirmed. A confirmation email will be sent shortly.
            </p>

            <div className="bg-muted/30 rounded-xl p-4 text-left space-y-2 mb-6">
              <p data-testid="confirm-service"><strong>Service:</strong> {booking.service?.name}</p>
              <p data-testid="confirm-date"><strong>Date:</strong> {format(booking.date, 'EEEE, d MMMM yyyy')}</p>
              <p data-testid="confirm-time"><strong>Time:</strong> {formatTime(booking.time)} — {formatTime(confirmation.end_time)}</p>
              <p data-testid="confirm-practitioner"><strong>Practitioner:</strong> {confirmation.practitioner_name || `${booking.practitioner?.first_name || ''} ${booking.practitioner?.last_name || ''}`.trim() || 'Assigned'}</p>
              <p data-testid="confirm-price"><strong>Price:</strong> {formatCurrency(booking.service?.price)}</p>
              {confirmation.clinic_address && (
                <p className="flex items-center gap-1 text-sm text-muted-foreground mt-2">
                  <MapPin size={14} /> {confirmation.clinic_address}
                </p>
              )}
            </div>

            <div className="space-y-2 mb-6" data-testid="calendar-actions">
              <p className="text-sm font-medium text-muted-foreground mb-2">Add to your calendar</p>
              <div className="flex gap-3 justify-center">
                <Button variant="outline" className="gap-2" onClick={() => window.open(getGoogleCalUrl(booking, confirmation), '_blank')} data-testid="add-google-cal-btn">
                  <GoogleLogo size={18} /> Google Calendar
                </Button>
                <Button variant="outline" className="gap-2" onClick={handleDownloadICS} data-testid="download-ics-btn">
                  <DownloadSimple size={18} /> Download .ics
                </Button>
              </div>
            </div>

            {/* Clinic contact */}
            <div className="bg-muted/20 rounded-xl p-4 text-sm text-left space-y-1 mb-4">
              <p className="font-medium mb-1">Need to cancel or reschedule?</p>
              {confirmation.clinic_email && (
                <p className="flex items-center gap-2 text-muted-foreground">
                  <EnvelopeSimple size={14} /> <a href={`mailto:${confirmation.clinic_email}`} className="underline">{confirmation.clinic_email}</a>
                </p>
              )}
              {confirmation.clinic_phone && (
                <p className="flex items-center gap-2 text-muted-foreground">
                  <Phone size={14} /> <a href={`tel:${confirmation.clinic_phone}`} className="underline">{confirmation.clinic_phone}</a>
                </p>
              )}
            </div>

            {confirmation.cancellation_policy && (
              <p className="text-xs text-muted-foreground mb-4">
                <strong>Cancellation Policy:</strong> {confirmation.cancellation_policy}
              </p>
            )}

            <Button className="btn-primary mt-2" onClick={() => window.location.reload()} data-testid="book-another-btn">
              Book Another Appointment
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ====== MAIN WIZARD ======
  return (
    <div className="max-w-2xl mx-auto py-8 px-4" data-testid="public-booking-page">
      {organisation && (
        <p className="text-center text-sm text-muted-foreground mb-4">{organisation.name}</p>
      )}

      {/* Progress steps */}
      <div className="flex items-center justify-center mb-8">
        {steps.map((step, index) => (
          <div key={step.id} className="flex items-center">
            <div
              className={`booking-step-number ${currentStep === step.id ? 'active' : ''} ${currentStep > step.id ? 'completed' : ''} ${currentStep < step.id ? 'pending' : ''}`}
              data-testid={`step-indicator-${step.id}`}
            >
              {currentStep > step.id ? <Check size={16} /> : step.id}
            </div>
            {index < steps.length - 1 && (
              <div className={`w-8 h-0.5 mx-1 ${currentStep > step.id ? 'bg-secondary' : 'bg-muted'}`} />
            )}
          </div>
        ))}
      </div>

      <h2 className="text-2xl font-serif font-semibold text-center mb-6">
        {steps[currentStep - 1].title}
      </h2>

      <Card className="dashboard-card">
        <CardContent className="pt-6">
          {/* ====== STEP 1: SELECT SERVICE (grouped accordion) ====== */}
          {currentStep === 1 && (
            <div className="space-y-2" data-testid="step-1-services">
              {services.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No services available at the moment.</p>
              ) : (
                <Accordion
                  type="single"
                  collapsible
                  defaultValue={orderedCategories[0] || undefined}
                  className="space-y-2"
                >
                  {orderedCategories.map((category) => {
                    const group = groupedServices[category];
                    const subEntries = Object.entries(group.bySub).sort(([a], [b]) => a.localeCompare(b));
                    const totalCount = group.items.length + subEntries.reduce((n, [, arr]) => n + arr.length, 0);
                    return (
                      <AccordionItem key={category} value={category} className="border rounded-xl bg-muted/20 px-3" data-testid={`category-accordion-${category}`}>
                        <AccordionTrigger className="py-3 hover:no-underline">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{category}</span>
                            <Badge variant="outline" className="text-xs">{totalCount}</Badge>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="pb-3">
                          <div className="space-y-3">
                            {subEntries.map(([sub, subServices]) => (
                              <div key={sub} data-testid={`public-sub-${sub}`}>
                                <p className="text-[11px] font-semibold uppercase tracking-wider text-secondary mb-1.5">{sub}</p>
                                <div className="space-y-1.5">
                                  {sortServices(subServices).map((service) => renderPublicServiceCard(service, booking, setBooking))}
                                </div>
                              </div>
                            ))}
                            {group.items.length > 0 && (
                              <div className={subEntries.length > 0 ? 'pt-1' : ''}>
                                {subEntries.length > 0 && <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">General</p>}
                                <div className="space-y-1.5">
                                  {sortServices(group.items).map((service) => renderPublicServiceCard(service, booking, setBooking))}
                                </div>
                              </div>
                            )}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    );
                  })}
                </Accordion>
              )}
            </div>
          )}

          {/* ====== STEP 2: CHOOSE PRACTITIONER ====== */}
          {currentStep === 2 && (
            <div className="space-y-3" data-testid="step-2-practitioners">
              {/* No preference option */}
              <div
                className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                  booking.noPreference ? 'border-secondary bg-secondary/5' : 'border-transparent bg-muted/30 hover:bg-muted/50'
                }`}
                onClick={() => setBooking({ ...booking, practitioner: null, noPreference: true, date: null, time: null })}
                data-testid="no-preference-option"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-secondary/10 flex items-center justify-center text-secondary">
                    <User size={20} />
                  </div>
                  <div>
                    <p className="font-medium">No preference</p>
                    <p className="text-sm text-muted-foreground">First available practitioner</p>
                  </div>
                </div>
              </div>

              {practitioners.length === 0 ? (
                <p className="text-center text-muted-foreground py-4">No practitioners available for this service.</p>
              ) : (
                practitioners.map((prac) => (
                  <div
                    key={prac.id}
                    className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      !booking.noPreference && booking.practitioner?.id === prac.id ? 'border-secondary bg-secondary/5' : 'border-transparent bg-muted/30 hover:bg-muted/50'
                    }`}
                    onClick={() => setBooking({ ...booking, practitioner: prac, noPreference: false, date: null, time: null })}
                    data-testid={`practitioner-option-${prac.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-secondary/10 flex items-center justify-center text-secondary font-medium">
                        {prac.first_name?.charAt(0)}{prac.last_name?.charAt(0)}
                      </div>
                      <div>
                        <p className="font-medium">{prac.first_name} {prac.last_name}</p>
                        <p className="text-sm text-muted-foreground">{prac.professional_title || ''}</p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* ====== STEP 3: PICK DATE & TIME ====== */}
          {currentStep === 3 && (
            <div className="space-y-6" data-testid="step-3-datetime">
              {datesLoading && (
                <p className="text-center text-sm text-muted-foreground flex items-center justify-center gap-2">
                  <SpinnerGap size={16} className="animate-spin" /> Checking availability...
                </p>
              )}
              <div className="flex justify-center">
                <Calendar
                  mode="single"
                  selected={booking.date}
                  onSelect={(date) => setBooking({ ...booking, date, time: null })}
                  disabled={(date) => {
                    if (isBefore(date, minDate) || isBefore(maxDate, date)) return true;
                    if (availableDates !== null) {
                      const dateStr = format(date, 'yyyy-MM-dd');
                      return !availableDates.has(dateStr);
                    }
                    return false;
                  }}
                  fromDate={minDate}
                  toDate={maxDate}
                  className="rounded-md border"
                />
              </div>

              {booking.date && (
                <div>
                  <p className="text-sm font-medium mb-3">Available times for {format(booking.date, 'EEEE, d MMMM')}</p>
                  {slotsLoading ? (
                    <p className="text-center text-muted-foreground py-4">Loading available times...</p>
                  ) : availableSlots.length === 0 ? (
                    <p className="text-center text-muted-foreground py-4">No available slots for this date. Please pick another day.</p>
                  ) : (
                    <div className="grid grid-cols-4 gap-2">
                      {availableSlots.map((slot) => (
                        <Button
                          key={slot}
                          variant={booking.time === slot ? 'default' : 'outline'}
                          className={booking.time === slot ? 'bg-secondary text-white' : ''}
                          onClick={() => setBooking({ ...booking, time: slot })}
                          data-testid={`time-slot-${slot}`}
                        >
                          {formatTime(slot)}
                        </Button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ====== STEP 4: YOUR DETAILS ====== */}
          {currentStep === 4 && (
            <div className="space-y-4" data-testid="step-4-details">
              {/* Returning client toggle */}
              <div className="flex items-center justify-between bg-muted/30 p-3 rounded-xl">
                <Label htmlFor="returning-toggle" className="text-sm cursor-pointer">Returning client?</Label>
                <Switch
                  id="returning-toggle"
                  checked={isReturning}
                  onCheckedChange={setIsReturning}
                  data-testid="returning-client-toggle"
                />
              </div>

              {isReturning && (
                <div className="flex gap-2">
                  <Input
                    type="email"
                    placeholder="Enter your email to look up"
                    value={lookupEmail}
                    onChange={(e) => setLookupEmail(e.target.value)}
                    className="input-field"
                    data-testid="client-lookup-email"
                  />
                  <Button variant="outline" onClick={handleClientLookup} disabled={lookupLoading || !lookupEmail} className="gap-2 shrink-0" data-testid="client-lookup-btn">
                    {lookupLoading ? <SpinnerGap size={16} className="animate-spin" /> : <MagnifyingGlass size={16} />}
                    Look up
                  </Button>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="firstName">First name *</Label>
                  <Input id="firstName" value={booking.firstName} onChange={(e) => setBooking({ ...booking, firstName: e.target.value })} className="input-field" required data-testid="booking-firstname" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName">Last name *</Label>
                  <Input id="lastName" value={booking.lastName} onChange={(e) => setBooking({ ...booking, lastName: e.target.value })} className="input-field" required data-testid="booking-lastname" />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email address *</Label>
                <Input id="email" type="email" value={booking.email} onChange={(e) => setBooking({ ...booking, email: e.target.value })} className="input-field" required data-testid="booking-email" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone number (optional)</Label>
                <Input id="phone" type="tel" value={booking.phone} onChange={(e) => setBooking({ ...booking, phone: e.target.value })} className="input-field" data-testid="booking-phone" />
              </div>

              {/* GDPR Consent - 3 separate checkboxes */}
              <div className="border-t border-border/30 pt-4 mt-4 space-y-3">
                <p className="text-sm font-medium">Data & Privacy</p>
                <div className="flex items-start gap-3">
                  <Checkbox id="gdprConsent" checked={booking.gdprConsent} onCheckedChange={(c) => setBooking({ ...booking, gdprConsent: !!c })} data-testid="gdpr-consent-checkbox" />
                  <Label htmlFor="gdprConsent" className="text-sm leading-snug cursor-pointer">
                    I consent to {organisation?.name || 'this clinic'} storing my personal data to manage my appointment. <span className="text-red-500">*</span>
                  </Label>
                </div>
                <div className="flex items-start gap-3">
                  <Checkbox id="marketingConsent" checked={booking.marketingConsent} onCheckedChange={(c) => setBooking({ ...booking, marketingConsent: !!c })} data-testid="marketing-consent-checkbox" />
                  <Label htmlFor="marketingConsent" className="text-sm leading-snug cursor-pointer">
                    I'd like to receive appointment reminders, offers, and updates via email (optional)
                  </Label>
                </div>
                <div className="flex items-start gap-3">
                  <Checkbox id="smsConsent" checked={booking.smsConsent} onCheckedChange={(c) => setBooking({ ...booking, smsConsent: !!c })} data-testid="sms-consent-checkbox" />
                  <Label htmlFor="smsConsent" className="text-sm leading-snug cursor-pointer">
                    I'd like to receive appointment reminders via SMS (optional)
                  </Label>
                </div>
              </div>
            </div>
          )}

          {/* ====== STEP 5: CONFIRM & BOOK ====== */}
          {currentStep === 5 && (
            <div className="space-y-4" data-testid="step-5-confirm">
              <h3 className="font-medium text-lg">Booking Summary</h3>
              <div className="bg-muted/30 rounded-xl p-4 space-y-3">
                <div className="flex justify-between py-2 border-b border-border/30">
                  <span className="text-muted-foreground">Service</span>
                  <span className="font-medium">{booking.service?.name}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-border/30">
                  <span className="text-muted-foreground">Date</span>
                  <span className="font-medium">{booking.date && format(booking.date, 'EEEE, d MMMM yyyy')}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-border/30">
                  <span className="text-muted-foreground">Time</span>
                  <span className="font-medium">{formatTime(booking.time)}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-border/30">
                  <span className="text-muted-foreground">Practitioner</span>
                  <span className="font-medium">
                    {booking.noPreference ? 'First available' : `${booking.practitioner?.first_name} ${booking.practitioner?.last_name}`}
                  </span>
                </div>
                <div className="flex justify-between py-2 border-b border-border/30">
                  <span className="text-muted-foreground">Duration</span>
                  <span className="font-medium">{booking.service?.duration} minutes</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-muted-foreground">Price</span>
                  <span className="font-semibold text-lg">{formatCurrency(booking.service?.price)}</span>
                </div>
              </div>

              {/* Deposit info */}
              {depositRequired && depositAmount > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid="deposit-notice">
                  <p className="text-sm font-medium text-amber-900 flex items-center gap-2">
                    <CurrencyGbp size={16} /> A deposit of {formatCurrency(depositAmount)} is required to secure your booking
                  </p>
                  {organisation?.can_accept_payments ? (
                    <p className="text-xs text-amber-700 mt-1">You'll be redirected to a secure payment page to pay your deposit after confirming.</p>
                  ) : (
                    <p className="text-xs text-amber-700 mt-1">Online deposit payment is not currently available. You may be asked to pay the deposit separately at the clinic.</p>
                  )}
                </div>
              )}

              <div className="bg-muted/30 rounded-xl p-4 space-y-2">
                <h4 className="font-medium">Your Details</h4>
                <p>{booking.firstName} {booking.lastName}</p>
                <p className="text-sm text-muted-foreground">{booking.email}</p>
                {booking.phone && <p className="text-sm text-muted-foreground">{booking.phone}</p>}
              </div>

              {organisation?.cancellation_policy && (
                <p className="text-xs text-muted-foreground">
                  <strong>Cancellation Policy:</strong> {organisation.cancellation_policy}
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Navigation */}
      <div className="flex justify-between mt-6">
        <Button variant="outline" onClick={() => setCurrentStep(currentStep - 1)} disabled={currentStep === 1} className="gap-2" data-testid="prev-step-btn">
          <CaretLeft size={18} /> Back
        </Button>

        {currentStep < 5 ? (
          <Button onClick={() => setCurrentStep(currentStep + 1)} disabled={!canProceed()} className="btn-primary gap-2" data-testid="next-step-btn">
            Continue <CaretRight size={18} />
          </Button>
        ) : (
          <Button onClick={handleSubmit} disabled={submitting} className="btn-primary gap-2" data-testid="confirm-booking-btn">
            {submitting ? 'Booking...' : 'Confirm Booking'}
            <Check size={18} />
          </Button>
        )}
      </div>
    </div>
  );
};
